<template>
  <NrModal
    primary-button-text="Close request"
    secondary-button-text="Cancel"
    title="Close request"
    :has-secondary-button="true"
    :show="!!active"
    @on-close-click="$emit('on-close')"
    @on-primary-click="submitForm"
    @on-secondary-click="$emit('on-close')"
  >
    <NrSelect
      append-to-body
      placeholder="Select a closure template"
      uid="closure_templates"
      multiple
      v-model="selectedClosureTemplates"
      :options="filteredClosureTemplates"
      :loading="dataFetchState.closure_templates.fetching"
      :hasMore="!dataFetchState.closure_templates.end"
      @input="setClosureTemplate"
      @load-more="handleClosureTemplateScroll"
      @search="filterClosureTemplates"
      @open="filterClosureTemplates"
    >
      <template v-slot:label>
        <span>Closure templates</span>
      </template>
    </NrSelect>
    <NrFormLabel>Closure template editor</NrFormLabel>
    <NrEditor
      bulletList
      orderedList
      link
      autofocus
      placeholder="Type your message here or select a template above."
      :rows="8"
      data-id="closure_text"
      v-model="closureText"
    />
    <NrFormSelect
      label="Request visibility"
      v-model="selectedRequestVisibility"
      :placeholder="
        selectedRequestVisibility && selectedRequestVisibility.label
          ? selectedRequestVisibility.label
          : ''
      "
      :options="reqVisibilities"
    />
  </NrModal>
</template>

<script lang="ts">
import { mapState, mapActions } from 'vuex'
import { throttle } from 'lodash'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrFormSelect from '@nextrequestco/components/src/components/NrFormSelect/nr-form-select'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements'
import {
  RequestVisibility,
  accountRequestVisibilities
} from '../../../store/globalModule'

interface ClosureTemplate {
  id: number
  label: string
  description: string
}

export default {
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  components: { NrModal, NrEditor, NrFormSelect, NrSelect, NrFormLabel },
  data() {
    return {
      selectedClosureTemplates: [],
      templates: [],
      closureText: '',
      selectedRequestVisibility: ''
    }
  },
  mounted() {
    this.setupCurrentReqVisiOption()
  },
  methods: {
    ...mapActions('request_show', ['fetchDataOptions']),

    filterClosureTemplates: throttle(function (searchText = '') {
      this.fetchDataOptions({ searchText, dataType: 'closure_templates' })
    }, 400),

    handleClosureTemplateScroll: throttle(function () {
      this.fetchDataOptions({
        searchText: this.dataFetchState.closure_templates.search_text,
        dataType: 'closure_templates'
      })
    }, 400),

    setClosureTemplate(temp: ClosureTemplate[]): void {
      if (!temp) {
        this.closureText = ''
        return
      }
      this.closureText = temp
        .map((template) => template.description)
        .join('<br />')
    },

    setupCurrentReqVisiOption(): void {
      const visi = this.reqVisibilities.find(
        (el: RequestVisibility) => el.value === this.request.visibility
      )
      this.selectedRequestVisibility = visi?.value
    },
    submitForm(): void {
      const params = {
        pretty_id: this.request.pretty_id
      }
      if (this.selectedRequestVisibility) {
        Object.assign(params, {
          visibility: this.selectedRequestVisibility
        })
      }
      if (this.selectedClosureTemplates.length) {
        Object.assign(params, {
          close_description: this.closureText ? this.closureText : '',
          close_reasons: this.selectedClosureTemplates.map(
            (template) => template.value
          )
        })
      } else {
        this.$toasted.error('You must select a reason for closing the request')
        return
      }
      this.$emit('on-submit', params)
    }
  },
  watch: {
    active(isActive) {
      if (isActive) {
        this.setupCurrentReqVisiOption()
      }
    }
  },
  computed: {
    ...mapState(['account']),
    ...mapState('request_show', ['request', 'dataFetchState', 'dataOptions']),
    reqVisibilities(): RequestVisibility[] {
      return accountRequestVisibilities(this.account)
    },

    filteredClosureTemplates() {
      return this.dataOptions.closure_templates.map((template) => ({
        description: template.description,
        label: template.label,
        value: template.id
      }))
    }
  }
}
</script>
